import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { NavigationType, useNavigate, useNavigationType } from "react-router-dom";
import Header from "../components/Header";
import SmartBasketBanner from "../components/SmartBasketBanner";
import HomeQuickActionsBar from "../components/HomeQuickActionsBar";
import DailyPlanCard from "../components/DailyPlanCard";
import HomeQuickCompareSection from "../components/HomeQuickCompareSection";
import ListFetchError from "../components/ListFetchError";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { apiRequest } from "../api/client";
import { normalizeGroceryProductPlatform, rememberGroceryProductView } from "../utils/groceryProductView";
import { buildGroceryAddToListBody } from "../utils/groceryPlatformData";
import { clearHomeQuickCompareMemory } from "../utils/catalogTabState";

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function calcSavingsFromComparison(product) {
  const comp = Array.isArray(product?.comparison) ? product.comparison : [];
  if (!comp.length) return null;
  const prices = comp
    .map((item) => parsePrice(item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price))
    .filter((p) => typeof p === "number" && Number.isFinite(p));
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const saving = max - min;
  return saving > 0 && Number.isFinite(saving) ? saving : null;
}

function FemtyzHome({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [quickCompareKey, setQuickCompareKey] = useState(0);
  const authToken = useAppSelector((s) => s.auth.token);
  const normalizedAuthToken = typeof authToken === "string" ? authToken.trim() : authToken;
  const isLoggedIn =
    Boolean(normalizedAuthToken) &&
    normalizedAuthToken !== "null" &&
    normalizedAuthToken !== "undefined";

  useLayoutEffect(() => {
    // Remove sticky tab query/storage left by older builds.
    try {
      const url = new URL(window.location.href);
      let changed = false;
      ["retailer", "price_mode", "offer"].forEach((key) => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      }
      localStorage.removeItem("femtyz_home_quick_compare_tabs");
      sessionStorage.removeItem("femtyz_home_quick_compare_tabs");
    } catch {
      // ignore
    }

    // Fresh home via menu/link → All + Base. Browser Back keeps previous selection.
    // Full page refresh also resets (in-memory store is wiped with the JS heap).
    if (navigationType !== NavigationType.Pop) {
      clearHomeQuickCompareMemory();
      setQuickCompareKey((k) => k + 1);
    }
  }, [navigationType]);

  useEffect(() => {
    const resetTabs = () => {
      clearHomeQuickCompareMemory();
      setQuickCompareKey((k) => k + 1);
    };
    window.addEventListener("femtyz:reset-home-tabs", resetTabs);
    return () => window.removeEventListener("femtyz:reset-home-tabs", resetTabs);
  }, []);
  const features = useAppSelector((s) => s.actowiz.features);
  const [bestOverallBasketState, setBestOverallBasketState] = useState({
    loading: false,
    error: "",
    groceryListId: null,
    savingMessage: "",
    bestOverallBasket: "",
    bannerMode: "new_account", // new_account | list_created | added_list | common_products
    showBestOverallBasketFallback: true,
    hero: {
      variant: "empty", // empty | success | limited | no_matches | locked
      savingsAmount: null,
      retailerName: "",
      basketTotal: null,
      matchedCount: null,
      totalItemCount: null,
      retailerCount: null,
      priceMode: "",
      lastUpdated: "",
      supportingCopy: "",
      isFromApi: false,
    },
  });
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const openPasteListFlow = useCallback(() => {
    navigate("/paste-list");
  }, [navigate]);

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  const loadBestOverallBasket = useCallback(async () => {
    if (!isLoggedIn) return;
    setBestOverallBasketState((prev) => ({ ...prev, loading: true, error: "" }));
    const res = await apiRequest("api/users/best_overall_basket", { method: "GET" });

    if (!res.ok) {
      setBestOverallBasketState((prev) => ({
        ...prev,
        loading: false,
        error: res.data?.message || "Failed to load best overall basket.",
      }));
      return;
    }

    const message = res.data?.message || "";
      const topSavingMessage = res.data?.saving_message || "";
      const savingAmountFlag = String(res.data?.saving_amount ?? "").trim().toLowerCase();
      const isSubscriptionLocked =
        savingAmountFlag === "no_subscription"
        || savingAmountFlag === "subscription_locked"
        || savingAmountFlag === "trial_locked";

      const bannerPayload =
        res.data?.homepage_smart_basket_banner
        || res.data?.smart_basket_banner
        || res.data?.homepage_smart_basket
        || null;

      const normalizeBannerVariant = (raw) => {
        const key = String(raw || "").trim().toLowerCase();
        if (!key) return "";
        if (["success", "smart_basket_success", "ready"].includes(key)) return "success";
        if (["limited", "limited_comparison", "partial", "weak_coverage"].includes(key)) return "limited";
        if (["no_matches", "no_matched_items", "no_matched_products", "zero_matches"].includes(key)) return "no_matches";
        if (["locked", "subscription_locked", "trial_locked", "gated"].includes(key)) return "locked";
        if (["empty", "visitor", "no_list", "no_active_list", "no_active_grocery_list"].includes(key)) return "empty";
        return "";
      };

      const parseCount = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const apiVariant =
        bannerPayload && typeof bannerPayload === "object"
          ? normalizeBannerVariant(
              bannerPayload?.state ?? bannerPayload?.variant ?? bannerPayload?.status
            )
          : "";

      const apiHero =
        apiVariant
          ? {
              variant: apiVariant,
              savingsAmount:
                parsePrice(
                  bannerPayload?.savingsAmount
                  ?? bannerPayload?.savings_amount
                  ?? bannerPayload?.saveUpTo
                  ?? bannerPayload?.save_up_to
                  ?? null
                ),
              retailerName:
                bannerPayload?.retailerName
                ?? bannerPayload?.retailer_name
                ?? bannerPayload?.bestRetailer
                ?? bannerPayload?.best_retailer
                ?? "",
              basketTotal:
                parsePrice(
                  bannerPayload?.basketTotal
                  ?? bannerPayload?.basket_total
                  ?? bannerPayload?.bestBasketTotal
                  ?? bannerPayload?.best_basket_total
                  ?? null
                ),
              matchedCount:
                parseCount(bannerPayload?.matchedCount ?? bannerPayload?.matched_count ?? bannerPayload?.comparableCount ?? bannerPayload?.comparable_count),
              totalItemCount:
                parseCount(bannerPayload?.totalItemCount ?? bannerPayload?.total_item_count ?? bannerPayload?.totalItems ?? bannerPayload?.total_items),
              retailerCount:
                parseCount(bannerPayload?.retailerCount ?? bannerPayload?.retailer_count),
              priceMode: bannerPayload?.priceMode ?? bannerPayload?.price_mode ?? "",
              lastUpdated: bannerPayload?.lastUpdated ?? bannerPayload?.last_updated ?? "",
              supportingCopy: bannerPayload?.supportingCopy ?? bannerPayload?.supporting_copy ?? "",
              isFromApi: true,
            }
          : null;

      const topLevelLockedHero = isSubscriptionLocked
        ? {
            variant: "locked",
            savingsAmount: null,
            retailerName: "",
            basketTotal: null,
            matchedCount: null,
            totalItemCount: null,
            retailerCount: null,
            priceMode: "",
            lastUpdated: "",
            supportingCopy: res.data?.saving_message || "",
            isFromApi: true,
          }
        : null;

      // If backend provides explicit copy at top-level, prefer it for locked state.
      const topLevelSupportingCopy =
        res.data?.saving_message
        || res.data?.supporting_copy
        || "";
      const topLevelPrimaryCopy = res.data?.primary_copy || "";
      const topLevelBodyCopy = res.data?.body_copy || "";

      // Case 4: common products found (backend provides these fields on grocery_lists)
      const groceryLists = Array.isArray(res.data?.grocery_lists) ? res.data.grocery_lists : [];
      const firstList = groceryLists[0] || null;
      const firstSavingMessage = firstList?.saving_message || "";
      const firstBestOverallBasket = firstList?.best_overall_basket || "";
      if (firstSavingMessage || firstBestOverallBasket) {
        const groceryListId = firstList?.grocery_list_id ?? firstList?.id ?? null;
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId,
          savingMessage: firstSavingMessage,
          bestOverallBasket: firstBestOverallBasket,
          bannerMode: "common_products",
          showBestOverallBasketFallback: true,
          hero:
            topLevelLockedHero
            || apiHero
            || {
              variant: "success",
              savingsAmount: parsePrice(res.data?.save_up_to_amount ?? res.data?.savings_amount ?? null),
              retailerName: "",
              basketTotal: null,
              matchedCount: null,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "",
              isFromApi: false,
            },
        });
        return;
      }

      // Case 1/2/3: no common products / or no lists yet (power_basket_list)
      const power = res.data?.power_basket_list || null;
      const groceryListId = power?.grocery_list_id ?? power?.id ?? null;
      const powerSavingMessage = power?.saving_message || "";

      if (topLevelLockedHero) {
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId: null,
          savingMessage: topLevelSupportingCopy,
          bestOverallBasket: "",
          bannerMode: "new_account",
          showBestOverallBasketFallback: false,
          hero: {
            ...topLevelLockedHero,
            // pass through copy via supportingCopy; the banner's locked copy is fixed,
            // but we keep backend support line.
            supportingCopy: topLevelSupportingCopy,
            primaryCopy: topLevelPrimaryCopy,
            bodyCopy: topLevelBodyCopy,
          },
        });
        return;
      }

      // Case 2: list exists but no products added yet (backend sends top-level saving_message)
      if (!power && topSavingMessage) {
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId: res.data?.grocery_list_id ?? res.data?.id ?? null,
          savingMessage: topSavingMessage,
          bestOverallBasket: "",
          bannerMode: "list_created",
          showBestOverallBasketFallback: false,
          hero:
            apiHero
            || {
              variant: "no_matches",
              savingsAmount: null,
              retailerName: "",
              basketTotal: null,
              matchedCount: 0,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "No matched products yet.",
              isFromApi: false,
            },
        });
        return;
      }

      const totalBasketAmount = parsePrice(power?.total_basket_amount ?? null);
      const lowestBasketText = typeof power?.lowest_basket === "string" ? power.lowest_basket.trim() : "";
      const backendBestOverallBasket =
        typeof power?.best_overall_basket === "string" ? power.best_overall_basket.trim() : "";

      const storeAmounts = power?.total_store_amounts && typeof power.total_store_amounts === "object"
        ? power.total_store_amounts
        : null;

      let secondLowestText = "";
      if (storeAmounts) {
        const entries = Object.entries(storeAmounts)
          .map(([store, amount]) => ({
            store: String(store || "").trim(),
            amount: parsePrice(amount),
          }))
          .filter((x) => x.store && typeof x.amount === "number");

        entries.sort((a, b) => a.amount - b.amount);
        if (entries.length >= 2) {
          const second = entries[1];
          secondLowestText = `2nd lowest basket: ${second.store} (£${second.amount.toFixed(2)})`;
        }
      }

      const bestOverallBasketParts = [];
      // Prefer backend-provided text when available.
      if (backendBestOverallBasket) {
        bestOverallBasketParts.push(backendBestOverallBasket);
      } else {
        if (secondLowestText) bestOverallBasketParts.push(secondLowestText);
        else if (lowestBasketText) bestOverallBasketParts.push(lowestBasketText);
        if (typeof totalBasketAmount === "number") {
          bestOverallBasketParts.push(`Total basket: £${totalBasketAmount.toFixed(2)}`);
        }
      }

      const bannerMode = groceryListId
        ? storeAmounts
          ? "added_list"
          : "list_created"
        : "new_account";

      // Case 1: new account (no list) → do not show basket summary line.
      const bestOverallBasketText =
        bannerMode === "new_account" ? "" : bestOverallBasketParts.join(" • ");

      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId,
        savingMessage: powerSavingMessage || topSavingMessage || message,
        bestOverallBasket: bestOverallBasketText,
        bannerMode,
        showBestOverallBasketFallback: bannerMode !== "new_account",
        hero:
          apiHero
          || (bannerMode === "new_account"
            ? {
              variant: "empty",
              savingsAmount: null,
              retailerName: "",
              basketTotal: null,
              matchedCount: null,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "No active grocery list yet.",
              isFromApi: false,
            }
            : storeAmounts
              ? {
                variant: "limited",
                savingsAmount: null,
                retailerName: "",
                basketTotal: null,
                matchedCount: null,
                totalItemCount: null,
                retailerCount: null,
                priceMode: "",
                lastUpdated: "",
                supportingCopy: bestOverallBasketText || "",
                isFromApi: false,
              }
              : {
                variant: "no_matches",
                savingsAmount: null,
                retailerName: "",
                basketTotal: null,
                matchedCount: 0,
                totalItemCount: null,
                retailerCount: null,
                priceMode: "",
                lastUpdated: "",
                supportingCopy: "No matched products yet.",
                isFromApi: false,
              }),
      });
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId: null,
        savingMessage: "",
        bestOverallBasket: "",
        bannerMode: "new_account",
        showBestOverallBasketFallback: false,
        hero: {
          variant: "empty",
          savingsAmount: null,
          retailerName: "",
          basketTotal: null,
          matchedCount: null,
          totalItemCount: null,
          retailerCount: null,
          priceMode: "",
          lastUpdated: "",
          supportingCopy: "No active grocery list yet.",
          isFromApi: false,
        },
      });
      return;
    }

    loadBestOverallBasket();
  }, [isLoggedIn, loadBestOverallBasket]);

  const handleAddToList = useCallback(async (product, meta = {}) => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = Array.isArray(minimalRes.data)
      ? minimalRes.data
      : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    let selectedListId = null;
    if (lists.length === 1) {
      selectedListId =
        lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
    } else {
      const selected = await chooseList(lists);
      if (!selected) {
        const cancelError = new Error("ADD_TO_LIST_CANCELLED");
        cancelError.code = "ADD_TO_LIST_CANCELLED";
        throw cancelError;
      }
      selectedListId =
        selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
    }
    if (!selectedListId) {
      throw new Error("Unable to select grocery list.");
    }

    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
    if (!productId) {
      throw new Error("Invalid product.");
    }

    const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
      method: "POST",
      body: buildGroceryAddToListBody(product),
    });

    if (!addRes.ok) {
      const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
      throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
    }

    const platform = normalizeGroceryProductPlatform(meta.platform || product?.platform);
    rememberGroceryProductView({
      listId: selectedListId,
      productId: String(productId),
      view: meta.detailView || (platform ? "single" : "compare"),
      platform,
    });
  }, [chooseList]);

  const offerTabLowestSaving = (() => {
    const latest12 = (Array.isArray(features) ? features : []).slice(0, 12);
    const savings = latest12
      .map((p) => calcSavingsFromComparison(p))
      .filter((n) => typeof n === "number" && Number.isFinite(n));
    return savings.length ? Math.min(...savings) : null;
  })();

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex w-full max-w-[90rem] flex-col gap-6 px-4 py-6 md:flex-row md:px-6">

        <section className="flex-1 space-y-8">
          {bestOverallBasketState.error && (
            <ListFetchError
              message={bestOverallBasketState.error}
              onRetry={loadBestOverallBasket}
              isLoading={bestOverallBasketState.loading}
            />
          )}

          <SmartBasketBanner
            variant={bestOverallBasketState.hero?.variant || "empty"}
            savingsAmount={
              bestOverallBasketState.hero?.savingsAmount
              ?? offerTabLowestSaving
              ?? null
            }
            retailerName={bestOverallBasketState.hero?.retailerName || ""}
            basketTotal={bestOverallBasketState.hero?.basketTotal ?? null}
            matchedCount={bestOverallBasketState.hero?.matchedCount ?? null}
            totalItemCount={bestOverallBasketState.hero?.totalItemCount ?? null}
            retailerCount={bestOverallBasketState.hero?.retailerCount ?? null}
            priceMode={bestOverallBasketState.hero?.priceMode || ""}
            lastUpdated={bestOverallBasketState.hero?.lastUpdated || ""}
            supportingCopy={
              bestOverallBasketState.hero?.supportingCopy
              || (bestOverallBasketState.savingMessage || bestOverallBasketState.bestOverallBasket || "")
            }
            onPrimary={() => {
              const variant = bestOverallBasketState.hero?.variant || "empty";
              if (variant === "locked") {
                navigate("/subscription");
                return;
              }
              if (variant === "empty" || variant === "no_matches") {
                openPasteListFlow();
                return;
              }
              const id = bestOverallBasketState.groceryListId;
              if (id) {
                navigate(
                  `/settings?tab=grocery&view=detail&grocery_list_id=${encodeURIComponent(String(id))}`
                );
              } else {
                navigate("/settings?tab=grocery");
              }
            }}
            onSecondary={() => {
              const variant = bestOverallBasketState.hero?.variant || "empty";
              if (variant === "locked") {
                navigate("/subscription");
                return;
              }
              if (variant === "no_matches") {
                const targetId = "home-listing-start";
                const el =
                  typeof document !== "undefined" ? document.getElementById(targetId) : null;
                if (el && typeof el.scrollIntoView === "function") {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  return;
                }
                navigate("/search");
                return;
              }
              navigate("/how-it-works");
            }}
          />


          {/* Hero / dashboard summary card */}
          <div className="rounded-3xl border border-[#E6EAF0] bg-white px-5 py-8 shadow-sm sm:px-5 sm:py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-xl">
                <p className="text-[16px] font-normal uppercase tracking-wide text-[#6E7B86]">
                  FEMTYZ Dashboard
                </p>
                <h1 className="mt-2 text-[28px] font-semibold leading-tight text-[#24303A]">
                  Find the cheapest groceries faster
                </h1>
                <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">
                  Compare prices across retailers, then add the best deals to your grocery list.
                </p>

                {/* <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/top-deals")}
                    className="rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
                  >
                    View Top Deals
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/features")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Browse Featured
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Search
                  </button>
                </div> */}
              </div>
              <DailyPlanCard />
            </div>
          </div>
          <HomeQuickActionsBar onOpenAuth={onOpenAuth} />
          <div id="home-listing-start" className="scroll-mt-24">
            <HomeQuickCompareSection
              key={quickCompareKey}
              onAddToList={handleAddToList}
              onOpenAuth={onOpenAuth}
            />
          </div>
          {/* <SupermarketTabs /> */}
        </section>
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[16px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FemtyzHome;